import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface LinkAnalysisPhotoRef {
  url?: string;
  image_url?: string;
  fid?: string;
  thumbnail?: string;
  label?: string;
}

export interface LinkAnalysisInvestigatePayload {
  root_type: 'phone' | 'email' | 'username';
  root_value: string;
  photo_refs?: LinkAnalysisPhotoRef[];
  max_layer?: number;
  ip_address?: string;
  location?: string;
  device?: string;
}

export interface LinkAnalysisDrillDownPayload {
  query_type?: 'phone' | 'email' | 'username' | 'name';
  query_value?: string;
  action_type?: 'osint_search';
  ip_address?: string;
  location?: string;
  device?: string;
}

export interface LinkAnalysisActionPayload {
  source_node_id: string;
  action_type: 'osint_search' | 'leak_search' | 'face_search' | 'ai_analysis';
  query_type: 'phone' | 'email' | 'username' | 'name' | 'image';
  query_value?: string;
  image_url?: string;
  ai_mode?: 'quick' | 'deep-cheap' | 'deep';
}

export interface LinkAnalysisManualNodePayload {
  label: string;
  type: 'person' | 'phone' | 'email' | 'username' | 'website';
  name: string;
  phone: string;
  email: string;
  username: string;
  image_url: string;
  profile_url: string;
  notes: string;
  details: Array<{label: string; value: string}>;
}

@Injectable({
  providedIn: 'root',
})
export class LinkAnalysisService {
  private readonly baseUrl = environment.baseUrl;

  constructor(private http: HttpClient) {}

  investigate(payload: LinkAnalysisInvestigatePayload): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/link-analysis/investigate`, payload);
  }

  createDrillDownWorkspace(payload: LinkAnalysisDrillDownPayload = {}): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/link-analysis/drill-down`, payload);
  }

  getHistory(
    page = 1,
    size = 10,
    searchKey?: string,
    searchValue?: string,
    startDate?: string,
    endDate?: string,
  ): Observable<any> {
    let params = new HttpParams().set('page', page).set('size', size);

    if (searchKey) {
      params = params.set('search_key', searchKey);
    }
    if (searchValue) {
      params = params.set('search_value', searchValue);
    }
    if (startDate) {
      params = params.set('start_date', startDate);
    }
    if (endDate) {
      params = params.set('end_date', endDate);
    }

    return this.http.get<any>(`${this.baseUrl}/link-analysis/history`, { params });
  }

  getResult(linkAnalysisId: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/link-analysis/result/${linkAnalysisId}`,
    );
  }

  regenerate(
    linkAnalysisId: string,
    photoRefs?: LinkAnalysisPhotoRef[],
    maxLayer?: number,
  ): Observable<any> {
    const payload: {
      photo_refs?: LinkAnalysisPhotoRef[];
      max_layer?: number;
    } = {};
    if (photoRefs?.length) {
      payload.photo_refs = photoRefs;
    }
    if (typeof maxLayer === 'number') {
      payload.max_layer = maxLayer;
    }
    return this.http.post<any>(
      `${this.baseUrl}/link-analysis/result/${linkAnalysisId}/regenerate`,
      payload,
    );
  }

  stopAutomation(linkAnalysisId: string): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/link-analysis/result/${linkAnalysisId}/stop`,
      {},
    );
  }

  runAction(linkAnalysisId: string, payload: LinkAnalysisActionPayload): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/link-analysis/result/${linkAnalysisId}/actions`,
      payload,
    );
  }

  addManualNode(linkAnalysisId: string, payload: LinkAnalysisManualNodePayload): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/link-analysis/result/${linkAnalysisId}/nodes`, payload);
  }
}
